import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('autoxium_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Articles API
export const articlesApi = {
  // Admin only (403 otherwise) -- see AdminPdfDownloadButton.
  downloadStaticArticlePdf: async (slug) => {
    const response = await axios.get(`${API_URL}/articles/static/${slug}/pdf`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  }
};

// Featured Articles API
export const featuredArticlesApi = {
  getFeaturedArticles: async () => {
    const response = await axios.get(`${API_URL}/featured-articles/`);
    return response.data;
  }
};

// How Does It Work? topics API (public)
export const howItWorksApi = {
  getTopics: async () => {
    const response = await axios.get(`${API_URL}/how-it-works/`);
    return response.data;
  },

  getTopic: async (topicId) => {
    const response = await axios.get(`${API_URL}/how-it-works/${topicId}`);
    return response.data;
  },

  // Admin only (403 otherwise) -- see AdminPdfDownloadButton.
  downloadTopicPdf: async (topicId) => {
    const response = await axios.get(`${API_URL}/how-it-works/${topicId}/pdf`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  }
};

// Page Content API (admin-editable Articles / Tool & Simulator User Guides)
export const pageContentApi = {
  getPageContent: async (slug) => {
    const response = await axios.get(`${API_URL}/page-content/${slug}`);
    return response.data;
  },

  getPublicArticleList: async () => {
    const response = await axios.get(`${API_URL}/page-content`);
    return response.data;
  }
};

// Community API
export const communityApi = {
  getCategories: async () => {
    const response = await axios.get(`${API_URL}/community/categories`);
    return response.data;
  },

  // Returns { items, total }. category: key or null; status: all|unanswered|solved; sort: newest|top
  getQuestions: async ({ skip = 0, limit = 20, category = null, status = 'all', sort = 'newest' } = {}) => {
    const params = { skip, limit, status, sort };
    if (category) params.category = category;
    const response = await axios.get(`${API_URL}/community/questions`, { params });
    return response.data;
  },

  // Auth header is optional here: when logged in the response carries the viewer's votes.
  getQuestion: async (questionId) => {
    const response = await axios.get(`${API_URL}/community/questions/${questionId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateQuestion: async (questionId, data) => {
    const response = await axios.put(`${API_URL}/community/questions/${questionId}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateAnswer: async (answerId, content) => {
    const response = await axios.put(`${API_URL}/community/answers/${answerId}`, { content }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteAnswer: async (answerId) => {
    const response = await axios.delete(`${API_URL}/community/answers/${answerId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // answerId null clears the accepted answer
  acceptAnswer: async (questionId, answerId) => {
    const response = await axios.post(`${API_URL}/community/questions/${questionId}/accept`, {
      answer_id: answerId
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  toggleVote: async (targetType, targetId) => {
    const response = await axios.post(`${API_URL}/community/votes`, {
      target_type: targetType, target_id: targetId
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  report: async (targetType, targetId, reason, details) => {
    const response = await axios.post(`${API_URL}/community/reports`, {
      target_type: targetType, target_id: targetId, reason, details: details || null
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  createQuestion: async (data) => {
    const response = await axios.post(`${API_URL}/community/questions`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  createAnswer: async (questionId, data) => {
    const response = await axios.post(`${API_URL}/community/questions/${questionId}/answers`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteQuestion: async (questionId) => {
    const response = await axios.delete(`${API_URL}/community/questions/${questionId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Search API
export const searchApi = {
  search: async (query, limit = 5) => {
    const response = await axios.get(`${API_URL}/search`, {
      params: { q: query, limit }
    });
    return response.data;
  }
};

// Topic Requests API
export const topicRequestsApi = {
  create: async (data) => {
    const response = await axios.post(`${API_URL}/topic-requests/`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Feedback API
export const feedbackApi = {
  create: async (data) => {
    const response = await axios.post(`${API_URL}/feedback/`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Newsletter API (public -- no login required to subscribe)
export const newsletterApi = {
  subscribe: async (email) => {
    const response = await axios.post(`${API_URL}/newsletter/subscribe`, { email }, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Favorites API (requires login, except `check` which silently no-ops when logged out)
export const favoritesApi = {
  add: async (contentType, contentId) => {
    const response = await axios.post(`${API_URL}/favorites/`, {
      content_type: contentType,
      content_id: contentId
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  remove: async (contentType, contentId) => {
    await axios.delete(`${API_URL}/favorites/${contentType}/${contentId}`, {
      headers: getAuthHeader()
    });
  },

  list: async (skip = 0, limit = 20) => {
    const response = await axios.get(`${API_URL}/favorites/`, {
      params: { skip, limit },
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Batch-checks which of `contentIds` are already favorited by the current user, so a
  // listing page can seed all its heart icons with one call instead of one per card.
  // Returns [] for a logged-out caller (matches the backend, which never 401s here).
  check: async (contentType, contentIds) => {
    if (!contentIds || contentIds.length === 0) return [];
    const response = await axios.get(`${API_URL}/favorites/check`, {
      params: { content_type: contentType, content_ids: contentIds.join(',') },
      headers: getAuthHeader()
    });
    return response.data.favorited_ids;
  }
};

// Admin API
export const adminApi = {
  getTopicRequests: async () => {
    const response = await axios.get(`${API_URL}/admin/topic-requests`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateTopicRequestStatus: async (requestId, status) => {
    const response = await axios.put(`${API_URL}/admin/topic-requests/${requestId}/status`, {
      status
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  // Users page. params: { skip, limit, search, status: ''|'active'|'suspended'|'deleted',
  // sort: name|email|role|created_at|last_login_at|login_count, order: asc|desc }
  getUsers: async (params) => {
    const response = await axios.get(`${API_URL}/admin/users`, {
      params,
      headers: getAuthHeader()
    });
    return response.data;
  },

  getUser: async (userId) => {
    const response = await axios.get(`${API_URL}/admin/users/${userId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  suspendUser: async (userId, reason) => {
    const response = await axios.post(`${API_URL}/admin/users/${userId}/suspend`, { reason: reason || null }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // action: 'unsuspend' | 'soft-delete' | 'restore'
  userAction: async (userId, action) => {
    const response = await axios.post(`${API_URL}/admin/users/${userId}/${action}`, null, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  hardDeleteUser: async (userId, confirmEmail) => {
    const response = await axios.delete(`${API_URL}/admin/users/${userId}/hard-delete`, {
      data: { confirm_email: confirmEmail },
      headers: getAuthHeader()
    });
    return response.data;
  },

  getVisitStats: async () => {
    const response = await axios.get(`${API_URL}/admin/stats/visits`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getSectionVisitStats: async (section) => {
    const response = await axios.get(`${API_URL}/admin/stats/visits/${section}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getFeedback: async (skip = 0, limit = 20, sort = 'desc') => {
    const response = await axios.get(`${API_URL}/admin/feedback`, {
      params: { skip, limit, sort },
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteFeedback: async (feedbackId) => {
    const response = await axios.delete(`${API_URL}/admin/feedback/${feedbackId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // status: OPEN | RESOLVED | DISMISSED | ALL
  getCommunityReports: async (status = 'OPEN', skip = 0, limit = 20) => {
    const response = await axios.get(`${API_URL}/admin/community/reports`, {
      params: { status, skip, limit },
      headers: getAuthHeader()
    });
    return response.data;
  },

  getCommunityOpenReportCount: async () => {
    const response = await axios.get(`${API_URL}/admin/community/reports/open-count`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateCommunityReportStatus: async (reportId, status) => {
    const response = await axios.put(`${API_URL}/admin/community/reports/${reportId}/status`, {
      status
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getNewsletterSubscribers: async (skip = 0, limit = 20, sort = 'desc') => {
    const response = await axios.get(`${API_URL}/admin/newsletter-subscribers`, {
      params: { skip, limit, sort },
      headers: getAuthHeader()
    });
    return response.data;
  },

  getFeaturedArticlesAdmin: async () => {
    const response = await axios.get(`${API_URL}/admin/featured-articles`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  createFeaturedArticle: async (data) => {
    const response = await axios.post(`${API_URL}/admin/featured-articles`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateFeaturedArticle: async (featuredArticleId, data) => {
    const response = await axios.put(`${API_URL}/admin/featured-articles/${featuredArticleId}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteFeaturedArticle: async (featuredArticleId) => {
    const response = await axios.delete(`${API_URL}/admin/featured-articles/${featuredArticleId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  restoreFeaturedArticle: async (featuredArticleId) => {
    const response = await axios.post(`${API_URL}/admin/featured-articles/${featuredArticleId}/restore`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  hardDeleteFeaturedArticle: async (featuredArticleId) => {
    const response = await axios.delete(`${API_URL}/admin/featured-articles/${featuredArticleId}/hard`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  reorderFeaturedArticles: async (items) => {
    const response = await axios.put(`${API_URL}/admin/featured-articles/reorder`, { items }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getHowItWorksTopicsAdmin: async () => {
    const response = await axios.get(`${API_URL}/admin/how-it-works`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getHowItWorksTopicAdmin: async (topicId) => {
    const response = await axios.get(`${API_URL}/admin/how-it-works/${topicId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  createHowItWorksTopic: async (data) => {
    const response = await axios.post(`${API_URL}/admin/how-it-works`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateHowItWorksTopic: async (topicId, data) => {
    const response = await axios.put(`${API_URL}/admin/how-it-works/${topicId}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateHowItWorksTopicCard: async (topicId, data) => {
    const response = await axios.patch(`${API_URL}/admin/how-it-works/${topicId}/card`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteHowItWorksTopic: async (topicId) => {
    const response = await axios.delete(`${API_URL}/admin/how-it-works/${topicId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  restoreHowItWorksTopic: async (topicId) => {
    const response = await axios.post(`${API_URL}/admin/how-it-works/${topicId}/restore`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  hardDeleteHowItWorksTopic: async (topicId) => {
    const response = await axios.delete(`${API_URL}/admin/how-it-works/${topicId}/hard`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteHowItWorksPageContent: async (slug) => {
    const response = await axios.delete(`${API_URL}/admin/how-it-works/page-content/${slug}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  restoreHowItWorksPageContent: async (slug) => {
    const response = await axios.post(`${API_URL}/admin/how-it-works/page-content/${slug}/restore`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  hardDeleteHowItWorksPageContent: async (slug) => {
    const response = await axios.delete(`${API_URL}/admin/how-it-works/page-content/${slug}/hard`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  reorderHowItWorksTopics: async (items) => {
    const response = await axios.put(`${API_URL}/admin/how-it-works/reorder`, { items }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  uploadImage: async (file, folder = 'featured') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    const response = await axios.post(`${API_URL}/admin/uploads/image`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getPageContentList: async ({ skip = 0, limit = 20, search = '', contentType, category, status, dateFrom, dateTo } = {}) => {
    const response = await axios.get(`${API_URL}/admin/page-content`, {
      params: {
        skip, limit, search,
        content_type: contentType || undefined,
        category: category || undefined,
        status: status || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      },
      headers: getAuthHeader()
    });
    return response.data;
  },

  getPageContentAdmin: async (slug) => {
    const response = await axios.get(`${API_URL}/admin/page-content/${slug}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  createPageContent: async (data) => {
    const response = await axios.post(`${API_URL}/admin/page-content`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getPageContentCategories: async (contentType) => {
    const response = await axios.get(`${API_URL}/admin/page-content-categories`, {
      params: { content_type: contentType || undefined },
      headers: getAuthHeader()
    });
    return response.data.categories;
  },

  updatePageContent: async (slug, data) => {
    const response = await axios.put(`${API_URL}/admin/page-content/${slug}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deletePageContent: async (slug) => {
    const response = await axios.delete(`${API_URL}/admin/page-content/${slug}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Visits / Trending API (public, no auth)
export const trendingApi = {
  // Fire-and-forget: records one visit to an item's detail page. Called by
  // PageVisitTracker, not by individual pages.
  recordVisit: async (section, itemId) => {
    await axios.post(`${API_URL}/visits/`, { section, item_id: itemId });
  },

  // Ranks `items` (each { item_id, updated_at }) by visit count within `section` and
  // returns up to 9 item_ids, most-trending first. Returns [] if the section has no
  // items or has never recorded a single visit.
  rank: async (section, items) => {
    const response = await axios.post(`${API_URL}/trending/rank`, { section, items });
    return response.data.item_ids;
  }
};

// Base URL of the backend itself (no /api suffix) — used to resolve served asset
// paths like image uploads (`/uploads/...`) returned by the API.
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
